import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Stethoscope, Lock, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { PrintPreview } from "@/components/PrintDoc";
import { DeathRecordDoc } from "@/components/VitalRecordDocs";
import {
  SEX_OPTS, RELIGION_OPTS, PLACE_OPTS, MED_ATTN_OPTS, MANNER_OPTS,
  EMPTY_ADDRESS, EMPTY_REGISTRAR, Sec, Grid, Txt, Num, Sel,
  AddressFields, RegistrarFields,
} from "./RecordsShared";

const YN_NA = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "not_applicable", l: "Not applicable" }];

const EMPTY = {
  date_of_death: "", sex: "",
  deceased: { name: "", aadhaar_no: "", date_of_birth: "", age: "" },
  mother: { name: "", aadhaar_no: "", mobile_no: "", email_id: "" },
  father: { name: "", aadhaar_no: "", mobile_no: "", email_id: "" },
  spouse: { name: "", aadhaar_no: "", date_of_birth: "", age: "", mobile_no: "", email_id: "" },
  address_at_death: { ...EMPTY_ADDRESS }, permanent_address: { ...EMPTY_ADDRESS },
  place_of_death: { type: "hospital_institution", institution_name: "", address: { ...EMPTY_ADDRESS } },
  informant: { name: "", aadhaar_no: "", mobile_no: "", email_id: "", address: { ...EMPTY_ADDRESS } },
  declaration: false, declaration_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), informant_signature: "", aadhaar_not_available_declared: false,
  residence: { type: "", name: "", sub_district: "", state_ut: "", pin_code: "" },
  religion: "", occupation: "", medical_attention: "", medically_certified: "",
  cause_of_death_name: "", female_death_pregnancy_related: "",
  smoking_years: "", tobacco_chew_years: "", arecanut_years: "", alcohol_years: "",
  registrar: { ...EMPTY_REGISTRAR },
};

const EMPTY_CLINICAL = {
  immediate_cause: { cause: "", interval_onset_to_death: "" },
  antecedent_cause_b: { cause: "", interval_onset_to_death: "" },
  antecedent_cause_c: { cause: "", interval_onset_to_death: "" },
  underlying_cause_d: { cause: "", interval_onset_to_death: "" },
  part_ii_other_conditions: "", manner_of_death: "",
  female_pregnant_at_death: "", female_death_within_42_days: "", pregnancy_contributed: "",
  surgery_performed: "", injury_manner: "",
  certifier_name: "", certifier_qualification: "", certifier_registration_no: "",
  certificate_form: "", certified_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
};

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
const deepSet = (setter) => (path) => (v) => setter((f) => {
  const keys = path.split("."); const clone = { ...f }; let node = clone;
  for (let i = 0; i < keys.length - 1; i++) { node[keys[i]] = { ...(node[keys[i]] || {}) }; node = node[keys[i]]; }
  node[keys[keys.length - 1]] = v; return clone;
});

export default function DeathRecords() {
  const { can } = useAuth();
  const manage = can("hospital.death.manage");
  const clinicalPerm = can("hospital.death.clinical");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("legal");
  const [form, setForm] = useState(EMPTY);
  const [clinical, setClinical] = useState(EMPTY_CLINICAL);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [printRecord, setPrintRecord] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/hospital/records/deaths", { params: { q: q || undefined } }); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const set = deepSet(setForm);
  const setC = deepSet(setClinical);

  const openNew = () => { setEditing(null); setForm(EMPTY); setClinical(EMPTY_CLINICAL); setTab("legal"); setFormError(""); setOpen(true); };
  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/hospital/records/deaths/${row.death_id}`);
      const r = data.record;
      setEditing(r);
      setForm({ ...EMPTY, ...r,
        smoking_years: r.smoking_years ?? "", tobacco_chew_years: r.tobacco_chew_years ?? "",
        arecanut_years: r.arecanut_years ?? "", alcohol_years: r.alcohol_years ?? "" });
      setClinical({ ...EMPTY_CLINICAL, ...(r.clinical || {}) });
      setTab("legal"); setFormError(""); setOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const payload = () => ({
    ...form,
    medical_attention: form.medical_attention || null,
    medically_certified: form.medically_certified || null,
    female_death_pregnancy_related: form.female_death_pregnancy_related || null,
    smoking_years: numOrNull(form.smoking_years), tobacco_chew_years: numOrNull(form.tobacco_chew_years),
    arecanut_years: numOrNull(form.arecanut_years), alcohol_years: numOrNull(form.alcohol_years),
  });

  const saveLegal = async (e) => {
    e.preventDefault();
    if (!form.date_of_death || !form.sex || !form.deceased.name) { setFormError("Date of death, deceased name and sex are required (Form No. 2)."); return; }
    setBusy(true);
    try {
      let saved;
      if (editing) {
        saved = await api.put(`/hospital/records/deaths/${editing.death_id}`, payload());
      } else {
        const { data } = await api.post("/hospital/records/deaths", payload());
        setEditing(data.record);
        saved = { data };
      }
      toast.success("Legal death report saved.");
      setPrintRecord(saved.data.record);
      if (!editing) load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  const openPrint = async (row) => {
    try {
      const { data } = await api.get(`/hospital/records/deaths/${row.death_id}`);
      setPrintRecord(data.record);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveClinical = async () => {
    if (!editing) { toast.error("Save the legal report first."); return; }
    setBusy(true);
    try {
      await api.put(`/hospital/records/deaths/${editing.death_id}/clinical`, clinical);
      toast.success("Medical cause-of-death certification saved (MCCD).");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const register = async (row) => {
    const regno = window.prompt("Enter the government Registration Number to mark this death as registered:");
    if (!regno) return;
    try {
      await api.post(`/hospital/records/deaths/${row.death_id}/status`,
        { status: "registered", registrar: { ...EMPTY_REGISTRAR, registration_no: regno, registration_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) } });
      toast.success("Death registered."); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeRow = async (row) => {
    if (!window.confirm("Delete this draft death record?")) return;
    try { await api.delete(`/hospital/records/deaths/${row.death_id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!can("hospital.death.view")) return <NoAccess what="Death records" />;
  const locked = editing?.status === "registered";
  const tabCls = (on) => `inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium border ${on ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`;

  return (
    <div data-testid="death-records-page">
      <PageHeader overline="Vital Records" title="Death Records — Form No. 2"
        subtitle="Government of India Death Report. Legal & statistical registration is separate from the confidential Medical Certification of Cause of Death (MCCD, Form 4/4A)."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-death-btn"><Plus className="h-4 w-4" /> New death report</button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, UHID, registration no. or date…"
          data-testid="death-search" className={`${inputCls} max-w-sm`} />
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="death-table" columns={["Record No.", "Deceased", "Sex", "Date of Death", "Certified", "MCCD", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No death reports yet" subtitle="Create your first Form No. 2 death report." testid="death-empty" />}
          renderRow={(r) => (
            <tr key={r.death_id} data-testid={`death-row-${r.death_id}`}>
              <Td className="font-medium text-slate-900">{r.record_no}</Td>
              <Td className="text-slate-800">{r.deceased_name}</Td>
              <Td className="text-slate-600">{r.sex}</Td>
              <Td className="text-slate-600">{r.date_of_death}</Td>
              <Td className="text-slate-600">{r.medically_certified === "yes" ? "Yes" : r.medically_certified === "no" ? "No" : "—"}</Td>
              <Td>{r.clinical_completed ? <span className="text-xs font-medium text-emerald-600">Completed</span> : <span className="text-xs text-slate-400">Pending</span>}</Td>
              <Td><StatusPill status={r.status} /></Td>
              {manage && <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => openEdit(r)} className={ghostBtnCls} data-testid={`edit-death-${r.death_id}`}>{r.status === "registered" ? "View" : "Edit"}</button>
                  <button onClick={() => openPrint(r)} className={ghostBtnCls}
                    data-testid={`print-death-${r.death_id}`}>
                    <Printer className="h-3.5 w-3.5" /> Print
                  </button>
                  {r.status !== "registered" && <button onClick={() => register(r)} className={ghostBtnCls} data-testid={`register-death-${r.death_id}`}>Register</button>}
                  {r.status !== "registered" && <button onClick={() => removeRow(r)} className={ghostBtnCls} data-testid={`delete-death-${r.death_id}`}>Delete</button>}
                </div>
              </Td>}
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => { setOpen(false); load(); }} wide
        title={editing ? `Death report ${editing.record_no || ""}` : "New death report (Form No. 2)"}
        subtitle={locked ? "Registered — read only" : "Legal registration & confidential medical certification are kept separate"}
        testid="death-modal">
        <div className="flex flex-wrap gap-3 mb-5">
          <button type="button" onClick={() => setTab("legal")} className={tabCls(tab === "legal")} data-testid="death-tab-legal">Legal & Statistical (Form No. 2)</button>
          <button type="button" onClick={() => setTab("clinical")} className={tabCls(tab === "clinical")} data-testid="death-tab-clinical">
            {clinicalPerm ? <Stethoscope className="h-4 w-4" /> : <Lock className="h-4 w-4" />} Medical Cause of Death (MCCD)
          </button>
        </div>

        {tab === "legal" ? (
          <form onSubmit={saveLegal} className="space-y-5">
            <p className="overline text-[#EA580C]">Legal Information</p>
            <Sec n={1} title="Deceased">
              <Grid cols={3}>
                <Txt label="Date of Death" type="date" value={form.date_of_death} onChange={set("date_of_death")} testid="death-dod" />
                <Sel label="Sex" value={form.sex} onChange={set("sex")} options={SEX_OPTS} testid="death-sex" />
                <Txt label="Name of deceased" value={form.deceased.name} onChange={set("deceased.name")} testid="death-name" />
                <Txt label="Aadhaar No. (if available)" value={form.deceased.aadhaar_no} onChange={set("deceased.aadhaar_no")} testid="death-aadhaar" />
                <Txt label="Date of Birth (if available)" type="date" value={form.deceased.date_of_birth} onChange={set("deceased.date_of_birth")} testid="death-dob" />
                <Txt label="Age" value={form.deceased.age} onChange={set("deceased.age")} testid="death-age" />
              </Grid>
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-700" data-testid="death-aadhaar-na">
                <input type="checkbox" checked={!!form.aadhaar_not_available_declared} onChange={(e) => set("aadhaar_not_available_declared")(e.target.checked)} />
                Aadhaar of the deceased is not available (declared)
              </label>
            </Sec>
            <Sec n={4} title="Mother's Details">
              <Grid cols={2}>
                <Txt label="Name" value={form.mother.name} onChange={set("mother.name")} testid="death-mother-name" />
                <Txt label="Aadhaar No." value={form.mother.aadhaar_no} onChange={set("mother.aadhaar_no")} testid="death-mother-aadhaar" />
                <Txt label="Mobile No." value={form.mother.mobile_no} onChange={set("mother.mobile_no")} testid="death-mother-mobile" />
                <Txt label="Email Id" value={form.mother.email_id} onChange={set("mother.email_id")} testid="death-mother-email" />
              </Grid>
            </Sec>
            <Sec n={5} title="Father's Details">
              <Grid cols={2}>
                <Txt label="Name" value={form.father.name} onChange={set("father.name")} testid="death-father-name" />
                <Txt label="Aadhaar No." value={form.father.aadhaar_no} onChange={set("father.aadhaar_no")} testid="death-father-aadhaar" />
                <Txt label="Mobile No." value={form.father.mobile_no} onChange={set("father.mobile_no")} testid="death-father-mobile" />
                <Txt label="Email Id" value={form.father.email_id} onChange={set("father.email_id")} testid="death-father-email" />
              </Grid>
            </Sec>
            <Sec n={6} title="Spouse's (husband / wife) Details">
              <Grid cols={3}>
                <Txt label="Name" value={form.spouse.name} onChange={set("spouse.name")} testid="death-spouse-name" />
                <Txt label="Aadhaar No." value={form.spouse.aadhaar_no} onChange={set("spouse.aadhaar_no")} testid="death-spouse-aadhaar" />
                <Txt label="Age (completed years)" value={form.spouse.age} onChange={set("spouse.age")} testid="death-spouse-age" />
                <Txt label="Date of Birth (if available)" type="date" value={form.spouse.date_of_birth} onChange={set("spouse.date_of_birth")} testid="death-spouse-dob" />
                <Txt label="Mobile No." value={form.spouse.mobile_no} onChange={set("spouse.mobile_no")} testid="death-spouse-mobile" />
                <Txt label="Email Id" value={form.spouse.email_id} onChange={set("spouse.email_id")} testid="death-spouse-email" />
              </Grid>
            </Sec>
            <Sec n={7} title="Address of the deceased at the time of death"><AddressFields value={form.address_at_death} onChange={set("address_at_death")} prefix="death-addr" /></Sec>
            <Sec n={8} title="Permanent address of the deceased"><AddressFields value={form.permanent_address} onChange={set("permanent_address")} prefix="death-perm" /></Sec>
            <Sec n={9} title="Place of death">
              <Grid cols={2}>
                <Sel label="Type" value={form.place_of_death.type} onChange={set("place_of_death.type")} options={PLACE_OPTS} testid="death-place-type" placeholder="Select" />
                <Txt label="Hospital / Institution name" value={form.place_of_death.institution_name} onChange={set("place_of_death.institution_name")} testid="death-place-name" />
              </Grid>
              {form.place_of_death.type !== "hospital_institution" && (
                <div className="mt-4"><AddressFields value={form.place_of_death.address} onChange={set("place_of_death.address")} prefix="death-place-addr" /></div>
              )}
            </Sec>
            <Sec n={10} title="Informant's Details">
              <Grid cols={2}>
                <Txt label="Name" value={form.informant.name} onChange={set("informant.name")} testid="death-inf-name" />
                <Txt label="Aadhaar No." value={form.informant.aadhaar_no} onChange={set("informant.aadhaar_no")} testid="death-inf-aadhaar" />
                <Txt label="Mobile No." value={form.informant.mobile_no} onChange={set("informant.mobile_no")} testid="death-inf-mobile" />
                <Txt label="Email Id" value={form.informant.email_id} onChange={set("informant.email_id")} testid="death-inf-email" />
              </Grid>
              <div className="mt-4"><AddressFields value={form.informant.address} onChange={set("informant.address")} prefix="death-inf-addr" /></div>
              <label className="mt-4 flex items-start gap-2 text-sm text-slate-700" data-testid="death-declaration">
                <input type="checkbox" className="mt-1" checked={!!form.declaration} onChange={(e) => set("declaration")(e.target.checked)} />
                <span>I have furnished true information to the best of my knowledge and belief (Declaration under Sec. 23, RBD Act, 1969).</span>
              </label>
              <div className="mt-4"><Grid cols={2}>
                <Txt label="Declaration date" type="date" value={form.declaration_date} onChange={set("declaration_date")} testid="death-decl-date" />
                <Txt label="Informant signature" value={form.informant_signature} onChange={set("informant_signature")} testid="death-signature" />
              </Grid></div>
            </Sec>

            <p className="overline text-[#EA580C] pt-2">Statistical Information</p>
            <Sec n={11} title="Town / Village of residence of the deceased">
              <Grid cols={3}>
                <Sel label="Type" value={form.residence.type} onChange={set("residence.type")} options={[{ v: "town", l: "Town" }, { v: "village", l: "Village" }]} testid="death-res-type" />
                <Txt label="Name" value={form.residence.name} onChange={set("residence.name")} testid="death-res-name" />
                <Txt label="Sub-district" value={form.residence.sub_district} onChange={set("residence.sub_district")} testid="death-res-sub" />
                <Txt label="State / UT" value={form.residence.state_ut} onChange={set("residence.state_ut")} testid="death-res-state" />
                <Txt label="PIN Code" value={form.residence.pin_code} onChange={set("residence.pin_code")} testid="death-res-pin" />
              </Grid>
            </Sec>
            <Sec n={12} title="Religion, occupation & medical attention">
              <Grid cols={2}>
                <Sel label="Religion" value={form.religion} onChange={set("religion")} options={RELIGION_OPTS} testid="death-religion" />
                <Txt label="Occupation of the deceased" value={form.occupation} onChange={set("occupation")} testid="death-occupation" />
                <Sel label="Type of Medical Attention received before death" value={form.medical_attention} onChange={set("medical_attention")} options={MED_ATTN_OPTS} testid="death-medattn" />
                <Sel label="Was the cause of death medically certified?" value={form.medically_certified} onChange={set("medically_certified")} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]} testid="death-certified" />
              </Grid>
              <div className="mt-4"><Field label="Name of Disease or Actual Cause of Death (for all deaths, whether certified or not)">
                <textarea className={inputCls} rows={2} data-testid="death-cause-name" value={form.cause_of_death_name}
                  onChange={(e) => set("cause_of_death_name")(e.target.value)} />
              </Field></div>
            </Sec>
            <Sec n={17} title="Female death & habitual history">
              <Grid cols={2}>
                <Sel label="If female: death while pregnant / at delivery / within 6 weeks after?" value={form.female_death_pregnancy_related} onChange={set("female_death_pregnancy_related")} options={YN_NA} testid="death-female-preg" />
                <div />
                <Num label="Habitual smoking — for how many years?" value={form.smoking_years} onChange={set("smoking_years")} testid="death-smoking" />
                <Num label="Habitual tobacco chewing — years?" value={form.tobacco_chew_years} onChange={set("tobacco_chew_years")} testid="death-tobacco" />
                <Num label="Habitual arecanut / pan masala — years?" value={form.arecanut_years} onChange={set("arecanut_years")} testid="death-arecanut" />
                <Num label="Habitual alcohol — years?" value={form.alcohol_years} onChange={set("alcohol_years")} testid="death-alcohol" />
              </Grid>
            </Sec>
            <Sec title="Registrar (to be filled by the Registrar)">
              <RegistrarFields value={form.registrar} onChange={set("registrar")} prefix="death-reg" />
            </Sec>

            {formError && <p className="text-sm text-red-600" data-testid="death-form-error">{formError}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setOpen(false); load(); }} className={ghostBtnCls}>Close</button>
              {!locked && <button disabled={busy} className={primaryBtnCls} data-testid="save-death-btn">{busy ? "Saving…" : editing ? "Save legal report" : "Create & continue"}</button>}
            </div>
          </form>
        ) : (
          <div className="space-y-5" data-testid="death-clinical-panel">
            {!clinicalPerm ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center" data-testid="clinical-locked">
                <Lock className="h-6 w-6 text-amber-500 mx-auto" />
                <p className="mt-3 font-semibold text-slate-800">Confidential — Medical Certification of Cause of Death</p>
                <p className="mt-1 text-sm text-slate-600">This section is restricted to certifying medical officers. You do not have the clinical certification permission.</p>
              </div>
            ) : !editing ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600" data-testid="clinical-need-legal">
                Save the legal death report first, then certify the medical cause of death here.
              </div>
            ) : (
              <>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  MCCD (Form 4 institutional / Form 4A non-institutional). This confidential block is stored separately from the legal report and never shown to non-clinical staff.
                </div>
                <Sec title="Part I — Cause of death (sequence directly leading to death)">
                  <div className="space-y-3">
                    {[["a", "immediate_cause", "Immediate cause"], ["b", "antecedent_cause_b", "Antecedent cause"], ["c", "antecedent_cause_c", "Antecedent cause"], ["d", "underlying_cause_d", "Underlying cause"]].map(([k, key, label]) => (
                      <Grid cols={2} key={key}>
                        <Txt label={`(${k}) ${label}`} value={clinical[key].cause} onChange={setC(`${key}.cause`)} testid={`mccd-${k}-cause`} />
                        <Txt label="Interval between onset and death" value={clinical[key].interval_onset_to_death} onChange={setC(`${key}.interval_onset_to_death`)} testid={`mccd-${k}-interval`} />
                      </Grid>
                    ))}
                  </div>
                </Sec>
                <Sec title="Part II — Other significant conditions">
                  <Field label="Other significant conditions contributing to death but not related to the disease/condition causing it">
                    <textarea className={inputCls} rows={2} data-testid="mccd-part2" value={clinical.part_ii_other_conditions} onChange={(e) => setC("part_ii_other_conditions")(e.target.value)} />
                  </Field>
                </Sec>
                <Sec title="Statistical & manner of death">
                  <Grid cols={2}>
                    <Sel label="Manner of death" value={clinical.manner_of_death} onChange={setC("manner_of_death")} options={MANNER_OPTS} testid="mccd-manner" />
                    <Txt label="Surgery performed (if any) & date" value={clinical.surgery_performed} onChange={setC("surgery_performed")} testid="mccd-surgery" />
                    <Txt label="If accident/injury — manner of injury" value={clinical.injury_manner} onChange={setC("injury_manner")} testid="mccd-injury" />
                  </Grid>
                </Sec>
                {form.sex === "Female" && (
                  <Sec title="Maternal death details (female)">
                    <Grid cols={3}>
                      <Sel label="Pregnant at the time of death?" value={clinical.female_pregnant_at_death} onChange={setC("female_pregnant_at_death")} options={YN_NA} testid="mccd-preg-death" />
                      <Sel label="Died within 42 days of termination of pregnancy?" value={clinical.female_death_within_42_days} onChange={setC("female_death_within_42_days")} options={YN_NA} testid="mccd-42days" />
                      <Sel label="Did pregnancy contribute to death?" value={clinical.pregnancy_contributed} onChange={setC("pregnancy_contributed")} options={YN_NA} testid="mccd-preg-contrib" />
                    </Grid>
                  </Sec>
                )}
                <Sec title="Certifier">
                  <Grid cols={2}>
                    <Txt label="Certifying doctor name" value={clinical.certifier_name} onChange={setC("certifier_name")} testid="mccd-cert-name" />
                    <Txt label="Qualification" value={clinical.certifier_qualification} onChange={setC("certifier_qualification")} testid="mccd-cert-qual" />
                    <Txt label="Medical registration No." value={clinical.certifier_registration_no} onChange={setC("certifier_registration_no")} testid="mccd-cert-reg" />
                    <Sel label="Certificate form" value={clinical.certificate_form} onChange={setC("certificate_form")} options={[{ v: "form_4", l: "Form 4 — Institutional" }, { v: "form_4a", l: "Form 4A — Non-institutional" }]} testid="mccd-form" />
                    <Txt label="Certified date" type="date" value={clinical.certified_date} onChange={setC("certified_date")} testid="mccd-date" />
                  </Grid>
                </Sec>
                <div className="flex justify-end">
                  <button type="button" disabled={busy} onClick={saveClinical} className={primaryBtnCls} data-testid="save-clinical-btn">{busy ? "Saving…" : "Save medical certification"}</button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
      <PrintPreview open={Boolean(printRecord)} onClose={() => setPrintRecord(null)}
        title={`Death record ${printRecord?.record_no || ""}`} paperSize="A4"
        subtitle="Saved record — printing is optional" testid="death-print-preview">
        <DeathRecordDoc record={printRecord} />
      </PrintPreview>
    </div>
  );
}
